import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Eye, EyeOff } from "lucide-react";
import { api, fileUrl, formatApiError, uploadFile } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AsyncListShell } from "@/components/admin/AsyncListShell";
import { AgencyAdvisors } from "@/components/admin/AgencyAdvisors";

const CATEGORIAS = ["Financieras y bancos", "Mecánicos", "Hojalatería y pintura", "Agencias de autos nuevos", "Otro"];
const EMPTY = { categoria: "Financieras y bancos", nombre: "", descripcion: "", logo_path: null, visible_frontend: true, agencia_activa: true };
const AGENCY = "Agencias de autos nuevos";

export default function PartnersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/partners"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((it) => {
    if (catFilter !== "all" && it.categoria !== catFilter) return false;
    if (!q.trim()) return true;
    return it.nombre.toLowerCase().includes(q.toLowerCase());
  }), [items, q, catFilter]);

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar socio?")) return;
    try { await api.delete(`/partners/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  async function toggleVisible(p) {
    try { await api.patch(`/partners/${p.id}`, { visible_frontend: !p.visible_frontend }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="admin-partners">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Módulo 04</p>
          <h1 className="mt-1 font-serif text-3xl text-[#3E5641] md:text-4xl">Socios comerciales</h1>
          <p className="mt-1 text-sm text-[#6B7678]">Los marcados como visibles aparecen en el home público, agrupados por categoría.</p>
        </div>
        <button className="btn-primary" onClick={() => setEditing({ ...EMPTY })} data-testid="partner-new-btn"><Plus size={16} /> Nuevo socio</button>
      </header>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input placeholder="Buscar por nombre..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="partner-search" />
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-64" data-testid="partner-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <AsyncListShell
        loading={loading}
        isEmpty={filtered.length === 0}
        loadingTestId="partner-loading"
        emptyTestId="partner-empty"
        emptyLabel="Sin socios aún."
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <div key={p.id} className="flex gap-4 border border-[#3E5641]/10 bg-white p-5" data-testid={`partner-card-${p.id}`}>
              <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center border border-[#3E5641]/10 bg-[#F1F3F3]">
                {p.logo_path ? (
                  <img src={fileUrl(p.logo_path)} alt="" className="max-h-16 max-w-full object-contain" />
                ) : (
                  <span className="mono text-xs text-[#6B7678]">sin logo</span>
                )}
              </div>
              <div className="flex-1">
                <p className="mono text-[10px] uppercase tracking-widest text-[#6B7678]">{p.categoria}</p>
                <p className="font-serif text-lg text-[#3E5641]">{p.nombre}</p>
                <p className="mt-1 text-xs text-[#6B7678]">{p.descripcion || "—"}</p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={() => toggleVisible(p)}
                    className="inline-flex items-center gap-1 text-xs text-[#3E5641] hover:text-[#A0553F]"
                    data-testid={`partner-toggle-${p.id}`}
                  >
                    {p.visible_frontend ? <><Eye size={12} /> Visible</> : <><EyeOff size={12} /> Oculto</>}
                  </button>
                  {p.categoria === AGENCY && (
                    <span className={`rounded-sm px-1.5 py-0.5 text-[10px] ${p.agencia_activa === false ? "bg-red-50 text-red-700" : "bg-green-50 text-green-800"}`} data-testid={`partner-agency-status-${p.id}`}>
                      {p.agencia_activa === false ? "Agencia inactiva" : "Agencia activa"}
                    </span>
                  )}
                  <button onClick={() => setEditing(p)} className="ml-auto p-1 hover:bg-[#F1F3F3]" data-testid={`partner-edit-${p.id}`}><Pencil size={14} /></button>
                  <button onClick={() => onDelete(p.id)} className="p-1 text-red-600 hover:bg-red-50" data-testid={`partner-delete-${p.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </AsyncListShell>

      {editing && <PartnerDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function PartnerDialog({ item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    categoria: item.categoria, nombre: item.nombre, descripcion: item.descripcion || "",
    logo_path: item.logo_path || null, visible_frontend: item.visible_frontend, agencia_activa: item.agencia_activa !== false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadFile(file, "partners");
      setF("logo_path", path);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        categoria: form.categoria,
        nombre: form.nombre.trim(),
        descripcion: form.descripcion?.trim() || null,
        logo_path: form.logo_path,
        visible_frontend: form.visible_frontend,
        ...(form.categoria === AGENCY ? { agencia_activa: form.agencia_activa } : {}),
      };
      if (isNew) await api.post("/partners", payload);
      else await api.patch(`/partners/${item.id}`, payload);
      toast.success(isNew ? "Socio creado" : "Socio actualizado");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isNew ? "Nuevo socio" : form.nombre}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="field-label">Categoría</Label>
            <Select value={form.categoria} onValueChange={(v) => setF("categoria", v)}>
              <SelectTrigger data-testid="partner-cat"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="field-label">Nombre</Label><Input value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} data-testid="partner-nombre" /></div>
          <div><Label className="field-label">Descripción corta</Label><Textarea rows={2} value={form.descripcion} onChange={(e) => setF("descripcion", e.target.value)} data-testid="partner-desc" /></div>
          <div>
            <Label className="field-label">Logo (PNG recomendado)</Label>
            <div className="flex items-center gap-4">
              {form.logo_path && <img src={fileUrl(form.logo_path)} alt="" className="h-14 w-14 border border-[#3E5641]/10 object-contain p-1" />}
              <label className="inline-flex cursor-pointer items-center gap-2 border border-dashed border-[#3E5641] px-4 py-2 text-sm">
                <Upload size={14} /> {uploading ? "Subiendo..." : (form.logo_path ? "Cambiar" : "Subir logo")}
                <input type="file" hidden accept="image/*" onChange={onLogo} data-testid="partner-logo-input" />
              </label>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-sm border border-[#3E5641]/10 p-3">
            <div>
              <p className="text-sm text-[#3E5641]">Visible en el home público</p>
              <p className="text-xs text-[#6B7678]">Aparece en la sección de socios comerciales.</p>
            </div>
            <Switch checked={form.visible_frontend} onCheckedChange={(v) => setF("visible_frontend", v)} data-testid="partner-visible" />
          </div>
          {form.categoria === AGENCY && (
            <div className="flex items-center justify-between rounded-sm border border-[#3E5641]/10 p-3">
              <div>
                <p className="text-sm text-[#3E5641]">Agencia activa</p>
                <p className="text-xs text-[#6B7678]">Una agencia inactiva no recibe leads nuevos.</p>
              </div>
              <Switch checked={form.agencia_activa} onCheckedChange={(v) => setF("agencia_activa", v)} data-testid="partner-agency-active" />
            </div>
          )}
          {!isNew && item.categoria === AGENCY && <AgencyAdvisors agencyId={item.id} />}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving || uploading} className="btn-primary disabled:opacity-60" data-testid="partner-save">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
